import styled, { keyframes, css } from "styled-components";
import bubblebg from "../images/bubblebannerbg.png";

export const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

export const pulseTicker = keyframes`
  0% { transform: scale(1); box-shadow: 0 0 0 0 rgba(67, 170, 139, 0.4); }
  70% { transform: scale(1.05); box-shadow: 0 0 0 8px rgba(67, 170, 139, 0); }
  100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(67, 170, 139, 0); }
`;

export const glow = keyframes`
  0% { box-shadow: 0 0 5px rgba(62, 143, 147, 0.2); }
  50% { box-shadow: 0 0 20px rgba(62, 143, 147, 0.4); }
  100% { box-shadow: 0 0 5px rgba(62, 143, 147, 0.2); }
`;

export const Container = styled.div`
  display: flex;
  background: transparent !important;
  flex-direction: column;
  gap: 2rem;
  padding: 1rem;
  max-width: 1200px;
  margin: 0 auto;
  @media (min-width: 992px) {
    padding: 2rem;
  }
`;

export const GlassCard = styled.div`
  background: ${({ theme }) => theme === 'dark' ? 'rgba(12, 30, 40, 0.7)' : 'rgba(255, 255, 255, 0.8)'};
  color: ${({ theme }) => theme === 'dark' ? '#fff' : '#111'};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid ${({ theme }) => theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(62, 143, 147, 0.2)'};
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  position: relative;

  &:hover {
    border-color: #3E8F93;
    transform: translateY(-2px);
  }
`;

export const AIHeaderCard = styled.div`
  background: linear-gradient(135deg, rgba(62, 143, 147, 0.9) 0%, rgba(13, 61, 64, 0.9) 100%), url(${bubblebg});
  background-size: cover;
  background-position: center;
  border-radius: 24px;
  padding: 2rem;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  box-shadow: 0 10px 30px rgba(62, 143, 147, 0.3);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 200%;
    height: 100%;
    background: linear-gradient(
      to right,
      transparent,
      rgba(255, 255, 255, 0.1),
      transparent
    );
    animation: ${shimmer} 5s infinite linear;
  }
`;

export const ScoreCircleWrapper = styled.div`
  position: relative;
  width: 150px;
  height: 150px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;

  svg {
    transform: rotate(-90deg);
  }

  circle {
    fill: none;
    stroke-width: 10;
    stroke-linecap: round;
  }

  .bg {
    stroke: ${props => props.color ? `${props.color}1a` : 'rgba(62, 143, 147, 0.1)'};
  }

  .progress {
    stroke: ${props => props.color || '#3E8F93'};
    stroke-dasharray: 440;
    stroke-dashoffset: ${props => 440 - (440 * props.percentage) / 100};
    transition: stroke-dashoffset 1s ease-out;
  }
`;

export const ScoreText = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: center;
  
  .value {
    font-size: 2.5rem;
    font-weight: 800;
    color: #3E8F93;
  }
  .label {
    font-size: 0.8rem;
    text-transform: uppercase;
    letter-spacing: 1px;
    opacity: 0.7;
  }
`;

export const StatGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  width: 100%;

  @media (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const StatCard = styled(GlassCard)`
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 0.5rem;

  .icon {
    font-size: 1.5rem;
    color: ${props => props.iconColor || '#3E8F93'};
    background: ${props => props.iconColor ? `${props.iconColor}1a` : 'rgba(62, 143, 147, 0.1)'};
    padding: 10px;
    border-radius: 12px;
    margin-bottom: 5px;
  }

  .value {
    font-size: 1.25rem;
    font-weight: 700;
  }

  .label {
    font-size: 0.85rem;
    opacity: 0.6;
  }
`;

export const TabNavigation = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
  background: ${({ theme }) => theme === 'dark' ? 'rgba(255,255,255,0.05)' : '#f0f4f5'};
  color: ${({ theme }) => theme === 'dark' ? '#fff' : '#111'};
  padding: 0.5rem;
  border-radius: 16px;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin; /* Show thin scrollbar on Firefox */
  
  &::-webkit-scrollbar {
    height: 4px; /* Show a tiny scrollbar on Chrome/Safari */
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'};
    border-radius: 10px;
  }

  @media (max-width: 1023px) {
    /* Add a subtle fade effect on the right to indicate more content */
    mask-image: linear-gradient(to right, black 85%, transparent 100%);
    -webkit-mask-image: linear-gradient(to right, black 85%, transparent 100%);
  }

  @media (min-width: 1024px) {
    justify-content: center;
    scrollbar-width: none;
    &::-webkit-scrollbar {
      display: none;
    }
    mask-image: none;
    -webkit-mask-image: none;
  }
`;

export const TabItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  border: none;
  background: ${props => props.active ? '#3E8F93' : 'transparent'};
  color: ${props => props.active ? 'white' : (props.theme === 'dark' ? '#fff' : '#111')};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;

  @media (max-width: 768px) {
    padding: 0.6rem 1rem;
    font-size: 0.85rem;
  }

  &:hover {
    background: ${props => props.active ? '#3E8F93' : 'rgba(62, 143, 147, 0.1)'};
  }

  svg {
    font-size: 1.1rem;
    color: ${props => props.active ? 'white' : '#3E8F93'};
  }
`;

export const Badge = styled.span`
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  background: ${props => props.variant === 'success' ? '#3E8F93' : '#FF6B6B'};
  color: white;
`;

export const ProgressBarTrack = styled.div`
  width: 100%;
  height: ${props => props.height || '8px'};
  background: ${props => props.theme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'};
  border-radius: 10px;
  overflow: hidden;
  position: relative;
`;

export const ProgressBarFill = styled.div`
  height: 100%;
  width: ${props => props.progress}%;
  background: ${props => props.color || 'linear-gradient(90deg, #3E8F93, #43AA8B)'};
  border-radius: 10px;
  transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const StepNavigator = styled.div`
  display: flex;
  justify-content: center;
  gap: 2rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    gap: 1rem;
    flex-wrap: wrap;
  }
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  color: ${props => props.active ? '#3E8F93' : 'rgba(0,0,0,0.3)'};
  opacity: ${props => props.active ? 1 : 0.6};
  
  span {
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: ${props => props.active ? '#3E8F93' : 'rgba(0,0,0,0.1)'};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 0.75rem;
  }
`;

export const ProgressWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  position: relative;
`;

export const ProgressTicker = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  border: 2.5px solid ${({ isDone, theme }) => isDone ? '#43AA8B' : (theme === 'dark' ? 'rgba(255,255,255,0.2)' : '#ddd')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background: ${({ isDone }) => isDone ? '#43AA8B' : 'transparent'};
  color: white;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  z-index: 1;

  ${({ isDone }) => !isDone && css`
    animation: ${pulseTicker} 2s infinite ease-in-out;
  `}

  &:hover {
    transform: scale(1.1);
    border-color: #43AA8B;
  }
`;

export const TickHint = styled.span`
  font-size: 0.75rem;
  font-weight: 700;
  color: #3E8F93;
  opacity: 0.8;
  animation: ${shimmer} 3s infinite linear;
  background: linear-gradient(90deg, #3E8F93, #43AA8B, #3E8F93);
  background-size: 200% auto;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  white-space: nowrap;
  letter-spacing: 0.5px;

  @media (max-width: 700px) {
    display: none;
  }
`;
